import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {
  Mail, Lock, ArrowRight, Eye, EyeOff, Check, Nfc,
  TrendingUp, Zap, Phone, MessageSquare, CalendarDays, Briefcase, ChevronRight
} from 'lucide-react'
import { useAuthStore } from '../../stores/authStore'

/* ================= DESIGN TOKENS ================= */
const C = {
  bg: '#f9f9ff',
  bgAlt: '#eff2ff',
  ink: '#000a1e',
  body: '#3c4148',
  primary: '#006b5f',
  primaryHover: '#005a50',
  navy: '#101f3f',
  gold: '#c9a24a',
}

const press =
  'transition-all duration-200 ease-out hover:scale-105 active:scale-95 cursor-pointer focus:outline-none focus-visible:ring-4 focus-visible:ring-[#006b5f]/30'

/* ================= LOGIN FORM ================= */
function LoginForm({ onSubmit, loading, error }) {
  const [form, setForm] = useState({ email: '', password: '' })
  const [showPwd, setShowPwd] = useState(false)
  const [remember, setRemember] = useState(false)

  const handleSubmit = (e) => {
    e.preventDefault()
    onSubmit(form.email, form.password, remember)
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Email */}
      <div>
        <label className="block text-sm font-semibold text-[#000a1e] mb-2">
          Adresse email professionnelle
        </label>
        <div className="relative">
          <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <input
            type="email"
            required
            placeholder="alexandre@votredomaine.fr"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            className="w-full pl-12 pr-5 py-4 bg-[#f9f9ff] rounded-2xl border border-slate-200 text-base text-[#000a1e] placeholder:text-slate-400
                       focus:outline-none focus:ring-4 focus:ring-[#006b5f]/15 focus:border-[#006b5f] transition-all"
          />
        </div>
      </div>

      {/* Password */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="text-sm font-semibold text-[#000a1e]">Mot de passe</label>
          <button
            type="button"
            className="text-sm font-semibold text-[#006b5f] hover:underline transition-all"
          >
            Mot de passe oublié ?
          </button>
        </div>
        <div className="relative">
          <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <input
            type={showPwd ? 'text' : 'password'}
            required
            placeholder="••••••••"
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            className="w-full pl-12 pr-12 py-4 bg-[#f9f9ff] rounded-2xl border border-slate-200 text-base text-[#000a1e] placeholder:text-slate-400
                       focus:outline-none focus:ring-4 focus:ring-[#006b5f]/15 focus:border-[#006b5f] transition-all"
          />
          <button
            type="button"
            onClick={() => setShowPwd(!showPwd)}
            className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-[#000a1e] transition-colors"
            aria-label={showPwd ? 'Masquer' : 'Afficher'}
          >
            {showPwd ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Remember */}
      <label className="flex items-center gap-3 cursor-pointer select-none">
        <input
          type="checkbox"
          checked={remember}
          onChange={(e) => setRemember(e.target.checked)}
          className="w-5 h-5 rounded accent-[#006b5f]"
        />
        <span className="text-base text-[#3c4148]">
          Rester connecté sur cet appareil pendant 30 jours
        </span>
      </label>

      {/* Error */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-100 rounded-xl text-sm text-red-600 animate-fade-in">
          {error}
        </div>
      )}

      {/* Submit */}
      <button
        type="submit"
        disabled={loading}
        className={`${press} group relative overflow-hidden w-full bg-[#006b5f] hover:bg-[#005a50] text-white font-display font-bold text-lg py-5 rounded-2xl shadow-lg shadow-[#006b5f]/20 disabled:opacity-60 flex items-center justify-center gap-3 shine`}
      >
        {loading ? (
          <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
        ) : (
          <>
            Se connecter à mon espace
            <ArrowRight className="w-5 h-5 transition-transform duration-200 group-hover:translate-x-1" />
          </>
        )}
      </button>
    </form>
  )
}

/* ================= PAGE ================= */
export default function Login() {
  const navigate = useNavigate()
  const { login, loading } = useAuthStore()
  const [error, setError] = useState('')

  const handleLogin = async (email, password, remember) => {
    setError('')
    try {
      await login(email, password)
      if (remember) localStorage.setItem('tapio_remember', '1')
      navigate('/app/overview')
    } catch (err) {
      setError(
        err.response?.data?.message ||
          'Erreur de connexion. Vérifiez vos identifiants.'
      )
    }
  }

  return (
    <div
      className="min-h-[calc(100vh-4rem)] flex items-stretch font-body text-[#3c4148] overflow-hidden"
      style={{ background: C.bg }}
    >
      {/* ================= ANIMATIONS CSS ================= */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:ital,wght@0,400;0,500;0,600;0,700;1,400&family=Plus+Jakarta+Sans:wght@500;600;700;800&display=swap');
        .font-display { font-family: 'Plus Jakarta Sans', system-ui, sans-serif; }
        .font-body { font-family: 'Inter', system-ui, sans-serif; }

        @keyframes float { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-14px)} }
        @keyframes floatSm { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-8px)} }
        @keyframes fadeIn { from{opacity:0} to{opacity:1} }
        @keyframes heroIn { from{opacity:0;transform:translateY(28px)} to{opacity:1;transform:translateY(0)} }
        @keyframes heroInX { from{opacity:0;transform:translateX(-20px)} to{opacity:1;transform:translateX(0)} }
        @keyframes blob { 0%,100%{transform:scale(1) translate(0,0)} 50%{transform:scale(1.15) translate(-20px,-16px)} }
        @keyframes blobSlow { 0%,100%{transform:scale(1) translate(0,0)} 50%{transform:scale(1.1) translate(20px,16px)} }
        @keyframes shine { 0%{transform:translateX(-120%) skewX(-20deg)} 100%{transform:translateX(320%) skewX(-20deg)} }

        .animate-float { animation: float 6s ease-in-out infinite; }
        .animate-float-slow { animation: float 7.5s ease-in-out infinite reverse; }
        .animate-float-sm { animation: floatSm 5s ease-in-out infinite; }
        .animate-float-sm-d { animation: floatSm 5.5s ease-in-out .8s infinite; }
        .animate-fade-in { animation: fadeIn .25s ease-out both; }
        .animate-hero { animation: heroIn .8s cubic-bezier(.2,.7,.2,1) both; }
        .animate-in-x { animation: heroInX .6s cubic-bezier(.2,.7,.2,1) both; }
        .animate-in-up { animation: heroIn .7s cubic-bezier(.2,.7,.2,1) both; }
        .animate-blob { animation: blob 12s ease-in-out infinite; }
        .animate-blob-slow { animation: blobSlow 15s ease-in-out infinite; }
        .shine::after {
          content: ''; position: absolute; top: 0; left: 0; height: 100%; width: 35%;
          background: linear-gradient(90deg, transparent, rgba(255,255,255,.35), transparent);
          transform: translateX(-120%) skewX(-20deg);
        }
        .shine:hover::after { animation: shine .9s ease-out; }

        @media (prefers-reduced-motion: reduce) {
          *,*::before,*::after {
            animation-duration: .01ms !important;
            animation-iteration-count: 1 !important;
            transition-duration: .01ms !important;
          }
        }
      `}</style>

      {/* ================= LEFT PANEL NAVY ================= */}
      <div
        className="hidden lg:flex lg:w-1/2 p-12 flex-col justify-between relative overflow-hidden"
        style={{ background: C.navy }}
      >
        {/* Blobs animés */}
        <div className="absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full bg-[radial-gradient(circle,rgba(20,120,110,0.35),transparent_65%)] animate-blob pointer-events-none" />
        <div className="absolute -bottom-40 -left-40 w-[500px] h-[500px] rounded-full bg-[radial-gradient(circle,rgba(201,162,74,0.2),transparent_65%)] animate-blob-slow pointer-events-none" />

        <div className="relative z-10 text-white">
          {/* Eyebrow */}
          <span className="animate-in-x inline-flex items-center gap-2 px-3.5 py-1.5 bg-white/10 rounded-full text-xs font-semibold mb-6 backdrop-blur border border-white/10">
            <span className="w-1.5 h-1.5 rounded-full bg-[#2dd4bf] animate-pulse" />
            PLATEFORME CERTIFIÉE 2025
          </span>

          {/* Title */}
          <h1
            className="animate-in-up font-display text-4xl xl:text-5xl font-bold leading-[1.08] tracking-tight mb-4"
            style={{ animationDelay: '100ms' }}
          >
            L'impact de votre <br />
            réseau commence <br />
            ici.
          </h1>

          <p
            className="animate-in-up text-base text-slate-300 mb-8 max-w-md leading-relaxed"
            style={{ animationDelay: '220ms' }}
          >
            Rejoignez plus de 15 000 dirigeants, créateurs et établissements qui
            révolutionnent leurs interactions physiques et numériques.
          </p>

          {/* ========== PHONE MOCKUP PREMIUM ========== */}
          <div
            className="animate-in-up relative flex justify-center pt-2 pb-4"
            style={{ animationDelay: '340ms' }}
          >
            <div className="relative w-[260px]">
              {/* Phone — flotte */}
              <div className="animate-float">
                <div className="rounded-[38px] border-[8px] border-[#000a1e] bg-white overflow-hidden ml-8 h-[400px] shadow-2xl transition-transform duration-500 hover:scale-[1.03]">
                  <div className="mx-auto mt-2 w-20 h-4 bg-[#000a1e] rounded-full" />

                  {/* Cover */}
                  <div className="mx-3 mt-2 h-[90px] rounded-2xl bg-gradient-to-br from-[#0b6a5d] to-[#3e8f83] relative">
                    <span className="absolute top-2 right-2 inline-flex items-center gap-1 bg-white/90 text-[9px] font-bold text-[#000a1e] px-2 py-0.5 rounded-full">
                      <Check className="w-3 h-3 text-[#006b5f]" /> Vérifié
                    </span>
                  </div>

                  {/* Avatar */}
                  <div className="relative -mt-7 mx-auto w-14 h-14 rounded-full bg-white border-4 border-white shadow" />

                  {/* Name */}
                  <div className="text-center px-3 mt-1">
                    <h3 className="font-display font-bold text-base text-[#000a1e]">
                      Mohammed Souamer
                    </h3>
                    <p className="text-[10px] font-medium text-[#3c4148]">
                      Directeur Associé • Nova Advisory
                    </p>
                    <p className="text-[9px] text-slate-500 mt-0.5">
                      Paris & Casablanca
                    </p>
                  </div>

                  {/* Save contact */}
                  <div className="mx-3 mt-3 flex gap-1.5">
                    <button className="flex-1 text-[10px] font-semibold rounded-full py-2 bg-[#006b5f] text-white hover:scale-105 active:scale-95 transition-all">
                      Enregistrer contact
                    </button>
                    <button className="w-8 h-8 rounded-full bg-[#e6ebff] flex items-center justify-center hover:scale-110 active:scale-90 transition-all">
                      <Phone className="w-3 h-3 text-[#000a1e]" />
                    </button>
                    <button className="w-8 h-8 rounded-full bg-[#dcf5ea] flex items-center justify-center hover:scale-110 active:scale-90 transition-all">
                      <MessageSquare className="w-3 h-3 text-[#006b5f]" />
                    </button>
                  </div>

                  {/* Actions */}
                  <div className="mx-3 mt-2 space-y-1.5">
                    {[
                      { Icon: CalendarDays, label: 'Réserver un entretien (30 min)' },
                      { Icon: Briefcase, label: 'Pitch Deck & Présentation' },
                    ].map(({ Icon, label }) => (
                      <button
                        key={label}
                        className="w-full flex items-center gap-2 bg-[#eff2ff] rounded-lg px-2.5 py-2 text-[10px] font-semibold text-[#000a1e] text-left transition-all hover:scale-[1.03] hover:bg-[#e3e9ff] active:scale-95"
                      >
                        <Icon className="w-3 h-3 text-[#006b5f]" />
                        <span className="flex-1">{label}</span>
                        <ChevronRight className="w-3 h-3 text-slate-500" />
                      </button>
                    ))}
                  </div>

                  <p className="text-center text-[8px] text-slate-500 mt-3">
                    <span className="inline-block w-1 h-1 rounded-full bg-[#006b5f] mr-1 animate-pulse" />
                    Tapio Instant Sync • vCard 4.0
                  </p>
                </div>
              </div>

              {/* Carte NFC flottante */}
              <div className="absolute -top-2 -left-10 w-[200px] h-[120px] animate-float-slow">
                <div className="w-full h-full rounded-xl bg-gradient-to-br from-[#161b2e] to-[#0a0e1c] shadow-2xl p-3 -rotate-[8deg] text-white transition-transform duration-500 hover:-rotate-[3deg] hover:scale-110 cursor-pointer">
                  <div className="flex items-center justify-between">
                    <span className="font-display font-bold tracking-widest text-xs flex items-center gap-1">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#2dd4bf] animate-pulse" />
                      TAPIO
                    </span>
                    <span className="w-5 h-3.5 rounded bg-gradient-to-br from-[#e6c76a] to-[#b8912f]" />
                  </div>
                  <div className="mt-6">
                    <p className="text-[8px] tracking-[0.2em] text-slate-400">
                      MEMBRE EXÉCUTIF
                    </p>
                    <p className="font-display font-semibold text-sm">
                      Mohammed Souamer
                    </p>
                    <p className="text-[7px] tracking-[0.15em] text-slate-500 mt-1">
                      NTAG216 MATTE STEEL
                    </p>
                  </div>
                </div>
              </div>

              {/* Chip +300% */}
              <div className="absolute top-[180px] -right-12 animate-float-sm">
                <div className="bg-white rounded-xl shadow-xl px-3 py-2.5 flex items-center gap-2 transition-transform duration-300 hover:scale-110">
                  <span className="w-7 h-7 rounded-full bg-slate-100 flex items-center justify-center">
                    <TrendingUp className="w-3.5 h-3.5 text-[#000a1e]" />
                  </span>
                  <div>
                    <p className="font-display font-bold text-sm leading-none text-[#000a1e]">
                      +300%
                    </p>
                    <p className="text-[9px] text-[#3c4148] mt-0.5">
                      Engagements vCard
                    </p>
                  </div>
                </div>
              </div>

              {/* Chip ⚡ < 0.2s */}
              <div className="absolute bottom-0 -left-6 animate-float-sm-d">
                <div className="bg-white rounded-xl shadow-xl px-3 py-2.5 flex items-center gap-2 transition-transform duration-300 hover:scale-110">
                  <span className="w-7 h-7 rounded-full bg-[#d5e8e4] flex items-center justify-center">
                    <Zap className="w-3.5 h-3.5 text-[#006b5f]" />
                  </span>
                  <div>
                    <p className="font-display font-bold text-sm leading-none text-[#000a1e]">
                      ⚡ &lt; 0.2s
                    </p>
                    <p className="text-[9px] text-[#3c4148] mt-0.5">
                      Transmission sans contact
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Testimonial */}
          <div
            className="animate-in-up max-w-md mt-4"
            style={{ animationDelay: '460ms' }}
          >
            <div className="flex items-center gap-1 text-[#f5d27a] mb-2 text-sm">
              {[1, 2, 3, 4, 5].map((i) => (
                <span key={i}>★</span>
              ))}
            </div>
            <p className="text-sm italic text-slate-300 leading-relaxed mb-3">
              « Tapio a transformé nos événements de prospection. Un simple tap, et
              nos fiches sont instantanément dans le CRM. »
            </p>
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-[#006b5f] flex items-center justify-center text-xs font-bold text-white">
                SL
              </div>
              <div>
                <p className="text-xs font-semibold text-white">Sophie Laurent</p>
                <p className="text-[10px] text-slate-400">
                  Head of Growth, FinTech Horizons
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer stats */}
        <div
          className="animate-in-up grid grid-cols-3 gap-3 relative z-10 mt-6 pt-5 border-t border-white/10"
          style={{ animationDelay: '580ms' }}
        >
          {[
            { icon: '⚡', label: '2 minutes', sub: 'Mise en route' },
            { icon: '💳', label: 'Zéro frais', sub: 'Sans abonnement' },
            { icon: '🛡️', label: 'RGPD Ready', sub: 'Données en France' },
          ].map((s, i) => (
            <div
              key={i}
              className="p-3 rounded-xl border border-white/10 backdrop-blur transition-all duration-300 hover:scale-105 hover:-translate-y-1"
              style={{ background: 'rgba(255,255,255,0.04)' }}
            >
              <div className="text-base mb-1">{s.icon}</div>
              <p className="text-[11px] font-semibold text-white">{s.label}</p>
              <p className="text-[9px] text-slate-400">{s.sub}</p>
            </div>
          ))}
        </div>
      </div>

      {/* ================= RIGHT FORM — AGRANDI ================= */}
      <div
        className="flex-1 flex items-center justify-center p-8 lg:p-16 xl:p-20"
        style={{ background: C.bg }}
      >
        <div className="w-full max-w-lg xl:max-w-xl">
          {/* Tabs premium */}
          <div className="flex items-center gap-1 mb-10 p-1.5 bg-[#eff2ff] rounded-2xl">
            <button
              type="button"
              className="flex-1 py-3.5 text-base font-semibold bg-white rounded-xl shadow-sm text-[#000a1e]"
            >
              Se connecter
            </button>
            <Link
              to="/register"
              className="flex-1 py-3.5 text-base font-medium text-slate-500 hover:text-[#000a1e] text-center transition-all rounded-xl"
            >
              Créer un compte
            </Link>
          </div>

          {/* Header */}
          <h2 className="font-display text-4xl xl:text-5xl font-bold text-[#000a1e] tracking-tight mb-3">
            Bon retour parmi nous
          </h2>
          <p className="text-base text-[#3c4148] mb-10 leading-relaxed">
            Accédez à la gestion de vos cartes connectées et statistiques de taps
            en direct.
          </p>

          {/* Google */}
          <button
            type="button"
            className={`${press} w-full py-4 bg-[#eff2ff] hover:bg-[#e3e9ff] rounded-xl text-base font-semibold text-[#000a1e] mb-8 flex items-center justify-center gap-3`}
          >
            <svg className="w-6 h-6" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Continuer avec Google Workspace
          </button>

          {/* Divider */}
          <div className="flex items-center gap-4 mb-8">
            <div className="flex-1 h-px bg-slate-200" />
            <span className="text-xs text-slate-400 font-semibold tracking-wider">
              OU AVEC VOTRE EMAIL
            </span>
            <div className="flex-1 h-px bg-slate-200" />
          </div>

          {/* Login Form (agrandi) */}
          <LoginForm onSubmit={handleLogin} loading={loading} error={error} />

          {/* Info card — activation NFC */}
          <div
            className="mt-8 p-5 rounded-2xl flex items-center gap-4 border border-[#eff2ff] transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_18px_44px_rgba(15,23,42,0.10)]"
            style={{ background: C.bgAlt }}
          >
            <div className="w-14 h-14 rounded-2xl bg-[#000a1e] flex items-center justify-center flex-shrink-0">
              <Nfc className="w-6 h-6 text-[#2dd4bf]" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-semibold text-[#000a1e] mb-1">
                Vous avez reçu une carte physique Tapio ?
              </p>
              <p className="text-xs text-slate-500 leading-relaxed">
                Associez votre puce NFC en quelques secondes grâce à votre code
                d'activation.
              </p>
            </div>
            <button
              type="button"
              className={`${press} bg-white hover:bg-slate-50 text-[#000a1e] text-sm font-semibold px-4 py-2.5 rounded-xl border border-slate-200 flex-shrink-0`}
            >
              Activer
            </button>
          </div>

          {/* Trust badges */}
          <div className="flex items-center justify-center gap-6 mt-10 text-xs text-slate-400">
            <span className="flex items-center gap-2">
              <Check className="w-4 h-4 text-[#006b5f]" />
              Chiffrement SSL 256-bit
            </span>
            <span className="flex items-center gap-2">
              <Check className="w-4 h-4 text-[#006b5f]" />
              Conforme RGPD
            </span>
          </div>
        </div>
      </div>
    </div>
  )
}
